import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { canEditResource } from 'lib/ownership';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { NoTools } from './no-tool';
import {
  ToolCard,
  ToolsListSkeleton,
  ToolsSectionHeadingSkeleton,
  ViewMode,
  toolsGridClassName,
  toolsListPaginationClassName,
  toolsListPaginationControlsClassName,
  toolsSectionHeadingClassName,
} from './tools-shared';
import { ToolsPagination } from './tools-pagination';
import { useTranslation } from 'react-i18next';

export function ToolsList({
  data,
  isLoading,
  view,
  activeTab,
  onAddClick,
  onImportClick,
  onToolSelect,
  onDeleteTool,
  isDeletingToolId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  projectKey,
  isAdmin,
}: {
  data?: { tools: ITool[]; total_count: number; total_pages: number; page: number };
  isLoading: boolean;
  view: ViewMode;
  activeTab?: string;
  onAddClick?: () => void;
  onImportClick?: () => void;
  onToolSelect: (tool: ITool) => void;
  onDeleteTool: (tool: ITool) => void;
  isDeletingToolId?: string | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions: number[];
  projectKey: string;
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const userId = useCommonStore((state) => state.user?.id);
  const tools = data?.tools || [];
  const regularTools = tools.filter((tool) => !tool.is_open_tool);
  const blocksTools = tools.filter((tool) => tool.is_open_tool);
  const totalCount = data?.total_count || 0;
  const pageCount = Math.max(1, data?.total_pages ?? 1);

  const renderToolsGrid = (toolList: ITool[]) => (
    <div className={view === 'grid' ? toolsGridClassName : 'space-y-3'}>
      {toolList.map((tool) => {
        const toolCanEdit = canEditResource(tool, userId);
        return (
          <ToolCard
            key={tool.id}
            tool={tool}
            view={view}
            allowEdit
            allowDelete={toolCanEdit}
            onDelete={onDeleteTool}
            isDeleting={isDeletingToolId === tool.id}
            onSelect={onToolSelect}
            projectKey={projectKey}
            isAdmin={isAdmin}
          />
        );
      })}
    </div>
  );

  const getToolsHeading = () => {
    switch (activeTab) {
      case 'mcp-server':
      case 'mcp':
        return t('MCP_TOOLS', 'MCP Tools');
      case 'composio':
      case 'connector':
        return t('CONNECTOR_TOOLS') || 'Connector Tools';
      case 'open-connectors':
        return t('OPEN_CONNECTOR_TOOLS') || 'Open Connector Tools';
      case 'tool':
      case 'tools':
      default:
        return t('MY_TOOLS') || 'My Tools';
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {!isLoading && data && totalCount === 0 ? (
        <NoTools onAddClick={onAddClick} onImportClick={onImportClick} />
      ) : (
        <>
          {isLoading ? (
            <div className="flex flex-col gap-3">
              <ToolsSectionHeadingSkeleton />
              <ToolsListSkeleton view={view} />
            </div>
          ) : null}
          {!isLoading ? (
            <>
              {regularTools.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {/* <p className={toolsSectionHeadingClassName}>{getToolsHeading()}</p> */}
                  {renderToolsGrid(regularTools)}
                </div>
              ) : null}
              {blocksTools.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <p className={toolsSectionHeadingClassName}>{t('BLOCKS_TOOLS') || 'Blocks Tools'}</p>
                  {renderToolsGrid(blocksTools)}
                </div>
              ) : null}
            </>
          ) : null}
        </>
      )}

      {data && totalCount > 0 ? (
        <div className={toolsListPaginationClassName}>
          <span className="text-sm text-muted-foreground">
            {t('TOOLS_SHOWING_ENTRIES', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, totalCount),
              total: totalCount,
            })}
          </span>

          <div className={toolsListPaginationControlsClassName}>
            <ToolsPagination page={page} pageCount={pageCount} onPageChange={onPageChange} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
